// Shared builder for BonusCard props to ensure consistent template across pages
// Cards follow BonusCard typography: subtitle (small), title (big), highlight (medium), description (small)

export function buildPromoCards(promo, ctx = {}) {
  const {
    isVerified,
    firstTimeOffers = [],
    firstOffer = null, // optional single offer context (e.g., lobby)
    navigate,
    enabledGamesCount,
    displayCount,
    enabledProvidersCount,
    showAll = false, // when true, show all cards regardless of account status (for playground/testing)
  } = ctx;

  const cards = [];

  // Filter out welcome if verified (unless showAll is true)
  if (promo?.variant === "welcome") {
    if (showAll || !isVerified) {
      cards.push({
        key: "welcome",
        variant: promo.variant || "default",
        subtitle: promo.subtitle,
        title: promo.title,
        highlight: promo.highlight,
        description: promo.description,
        buttonText: promo.buttonText,
        onButtonClick:
          promo.link && navigate ? () => navigate(promo.link) : undefined,
        image: promo.image,
        id: promo.id,
      });
    }
    return cards;
  }

  // Replace placeholder with actual backend-driven 1st purchase cards
  if (promo?.id === "first_purchase") {
    const offers =
      Array.isArray(firstTimeOffers) && firstTimeOffers.length > 0
        ? firstTimeOffers
        : firstOffer
          ? [firstOffer]
          : [];

    // No offers loaded — render the static fallback. Covers anonymous
    // visitors (offers aren't fetched while logged out), the brief window
    // before the offers request resolves, and prod environments where the
    // first-time offer record hasn't been created yet. Anything is better
    // than silently dropping the card.
    if (offers.length === 0) {
      cards.push({
        key: "first_purchase_fallback",
        variant: promo.variant || "default",
        subtitle: promo.subtitle,
        title: promo.title,
        highlight: promo.highlight,
        description: promo.description,
        buttonText: promo.buttonText,
        onButtonClick:
          promo.link && navigate ? () => navigate(promo.link) : undefined,
        image: promo.image,
        id: promo.id,
      });
      return cards;
    }

    offers.forEach((o) => {
      const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : n);
      const fmtPrice = (n) => (typeof n === "number" ? n.toFixed(2) : n);
      cards.push({
        key: o.id,
        variant: "default",
        subtitle: String(o.name || "1st Purchase Bonus"), // small top line (auto uppercased by component)
        title:
          o?.bonus_percentage != null
            ? `${Number(o.bonus_percentage)}% BONUS COINS`
            : "BONUS COINS", // BIG middle line
        // no highlight to keep exactly 3 text sections
        description: `Get ${fmtNum(o.gold_coins)} gold, and ${fmtNum(o.sweep_coins)} Cluck Bucks for $${fmtPrice(o.usd_price)}`,
        buttonText: "Buy Now",
        onButtonClick: navigate
          ? () => navigate(`/checkout/${o.id}`)
          : undefined,
        // All first-purchase cards use the static /50%-bonus.png asset as
        // a single source of truth. The per-offer image_url field is left
        // alone in the DB but ignored here so the lobby/promotions pages
        // can't drift when admins forget to update it.
        image: promo.image,
        id: "first_purchase",
      });
    });
    return cards;
  }

  // HUGE GAME COLLECTION card with live counts
  if (promo?.id === "huge_collection") {
    const n =
      typeof enabledGamesCount === "number"
        ? enabledGamesCount
        : typeof displayCount === "number"
          ? displayCount
          : 0;
    const gamesText =
      n >= 1000 ? "1,000+" : Number.isFinite(n) ? n.toLocaleString() : "0";
    const providersText =
      typeof enabledProvidersCount === "number" ? enabledProvidersCount : 0;

    cards.push({
      key: promo.id,
      variant: promo.variant || "default",
      subtitle: promo.subtitle ?? promo.title, // small top line label
      title: `${typeof enabledGamesCount === "number" ? enabledGamesCount.toLocaleString() : gamesText} GAMES`, // big middle line
      highlight: undefined,
      description: `${typeof enabledGamesCount === "number" ? enabledGamesCount.toLocaleString() : gamesText} games across ${providersText} providers. New games added daily.`,
      buttonText: promo.buttonText,
      onButtonClick:
        promo.link && navigate ? () => navigate(promo.link) : undefined,
      image: promo.image,
      id: promo.id,
    });
    return cards;
  }

  // Default mapping
  cards.push({
    key: `static-${promo.id || promo.title}`,
    variant: promo.variant || "default",
    subtitle: promo.subtitle,
    title: promo.title,
    highlight: promo.highlight,
    description: promo.description,
    buttonText: promo.buttonText,
    onButtonClick:
      promo.link && navigate ? () => navigate(promo.link) : undefined,
    image: promo.image,
    id: promo.id,
  });

  return cards;
}
